import axios, {
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosError,
} from 'axios'
import applyCaseMiddleware from 'axios-case-converter'

import { ROUTES } from '@/config/routes'
import { IErrorDTO } from '@/DTOs/errorDTO'
import { isThrottledError } from '@/utils/api'

const AUTH_ROUTES = [
  '/auth/login/',
  '/auth/login/dev/',
  '/auth/refresh/',
  '/auth/logout/',
  '/auth/verify/',
]

let isRefreshing = false
let failedQueue: Array<{
  resolve: (value: unknown) => void
  reject: (error: unknown) => void
}> = []

const processQueue = (error: unknown) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(undefined)
    }
  })
  failedQueue = []
}

const getCSRFToken = (): string | null => {
  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('csrftoken='))
  return cookie ? cookie.split('=')[1] : null
}

const isAuthRoute = (url?: string): boolean => {
  return AUTH_ROUTES.some(route => url?.includes(route))
}

const isOnLoginPage = (): boolean => {
  return window.location.pathname.startsWith(ROUTES.LOGIN)
}

const redirectToLogin = () => {
  if (!isOnLoginPage()) {
    window.location.replace(ROUTES.LOGIN)
  }
}

const redirectToAccessDenied = () => {
  window.location.replace(ROUTES.ACCESS_DENIED)
}

const http = applyCaseMiddleware(
  axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    withCredentials: true,
    timeout: 150000,
  }),
  {
    ignoreHeaders: true,
  }
)

export const httpWithoutMiddleware = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  timeout: 150000,
})

const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const response = await httpWithoutMiddleware.post('/auth/refresh/')
    return response.status === 200
  } catch (error) {
    // 429 não é refresh inválido: a sessão continua e o erro sobe para a tela.
    if (isThrottledError(error)) {
      throw error
    }
    return false
  }
}

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
  const csrfToken = getCSRFToken()
  const method = config.method?.toLowerCase()

  if (csrfToken && ['post', 'put', 'patch', 'delete'].includes(method || '')) {
    config.headers['X-CSRFToken'] = csrfToken
  }

  return config
}

const responseInterceptor = (response: AxiosResponse) => response

const errorInterceptor = async (error: AxiosError): Promise<unknown> => {
  const originalRequest = error.config as InternalAxiosRequestConfig & {
    _retry?: boolean
  }

  if (error.response?.status === 401) {
    if (isAuthRoute(originalRequest?.url)) {
      throw error
    }

    if (originalRequest._retry) {
      redirectToLogin()
      throw error
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
      }).then(() => http(originalRequest))
    }

    originalRequest._retry = true
    isRefreshing = true

    try {
      const success = await refreshAccessToken()

      if (success) {
        processQueue(null)
        return http(originalRequest)
      }
    } catch (refreshError) {
      processQueue(refreshError)
      if (!isThrottledError(refreshError)) {
        redirectToLogin()
      }
      throw refreshError
    } finally {
      isRefreshing = false
    }

    processQueue(error)
    redirectToLogin()
    throw error
  }

  if (error.response?.status === 403 && !isAuthRoute(originalRequest?.url)) {
    redirectToAccessDenied()
    throw error
  }

  if (!error.response && error.code === 'ERR_NETWORK') {
    console.error('Network error - please check your connection')
  }

  throw error
}

const simpleErrorInterceptor = (error: AxiosError): Promise<never> => {
  if (error.response?.status === 401 && !isAuthRoute(error.config?.url)) {
    const errorData = error.response.data as IErrorDTO
    const errorCodes = errorData?.errors?.map(e => e.code) || []

    if (errorCodes.includes('not_authenticated')) {
      redirectToLogin()
    }
  }

  if (error.response?.status === 403 && !isAuthRoute(error.config?.url)) {
    redirectToAccessDenied()
  }

  return Promise.reject(error)
}

http.interceptors.request.use(requestInterceptor)
http.interceptors.response.use(responseInterceptor, errorInterceptor)

httpWithoutMiddleware.interceptors.request.use(requestInterceptor)
httpWithoutMiddleware.interceptors.response.use(
  responseInterceptor,
  simpleErrorInterceptor
)

export default http
